// Wariant Wise: przycisk "Listy zakupowe" w naglowku + popover ze stylem Wise UI.
// Logika identyczna jak w components/containers/ShoppingListButton, zmiana to nowy widok popovera.

import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import Popover from '@mui/material/Popover';
import { Trans, useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Cart, JournalText, Trash } from 'react-bootstrap-icons';

import { useDeleteShoppingList, useGetShoppingLists } from 'api';
import { IShoppingListListItem } from 'api/types';
import { Button, Link, Modal } from 'components/controls';
import { useRWD } from 'hooks';
import { WiseShoppingListPositions } from 'pages/wise/WiseShoppingLists/components';

import styles from 'theme/components/wise/WiseShoppingListButton/WiseShoppingListButton.module.scss';

const WiseShoppingListButton = () => {
  const { t } = useTranslation();
  const { isMobile } = useRWD();

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement | null>(null);
  const [currentShoppingListId, setCurrentShoppingListId] = useState<number | null>(null);
  const [itemToRemove, setItemToRemove] = useState<IShoppingListListItem | null>(null);

  const {
    data: shoppingListsData,
    refetch: refetchShoppingLists,
    isLoading: isListLoading
  } = useGetShoppingLists(
    { page: 1, limit: 9999 },
    { enabled: false, keepPreviousData: true }
  );

  const { mutate: deleteShoppingList, isLoading: isDeleting } = useDeleteShoppingList(
    itemToRemove?.id || 0,
    {
      onSuccess: () => {
        setItemToRemove(null);
        refetchShoppingLists();
      }
    }
  );

  useEffect(() => {
    if (popoverAnchor) {
      refetchShoppingLists();
    }
  }, [popoverAnchor]);

  const totalCount = shoppingListsData?.items?.length ?? 0;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-Wise-WiseShoppingListButton'
      )}>
      <div
        role="button"
        onClick={(e) => setPopoverAnchor(e.currentTarget)}
        className={classnames(styles.trigger, { [styles.triggerActive]: popoverAnchor })}>
        <JournalText /> <span>{t('Listy zakupowe')}</span>
      </div>

      <Popover
        anchorEl={popoverAnchor}
        open={!!popoverAnchor && !isMobile}
        onClose={() => setPopoverAnchor(null)}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ className: styles.popoverPaper }}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <div className={styles.panelTitle}>
              <Trans>Listy zakupowe</Trans>
              <span className={styles.panelCount}>({totalCount})</span>
            </div>
          </div>

          <div className={styles.list}>
            {isListLoading
              ? [...Array(3)].map((_, i) => (
                  <div key={i} className={classnames(styles.listItem, styles.listItemSkeleton)}>
                    <div className={styles.itemBody}>
                      <div className={styles.skeletonLine} style={{ width: '60%' }} />
                      <div
                        className={styles.skeletonLine}
                        style={{ width: '40%', marginTop: 6, height: 10 }}
                      />
                    </div>
                    <div className={styles.itemActions}>
                      <div className={styles.skeletonChip} />
                    </div>
                  </div>
                ))
              : shoppingListsData?.items.map((item) => {
                  const disabled = !item.products_count || item.blocked;
                  return (
                    <div key={item.id} className={styles.listItem}>
                      <div className={styles.itemBody}>
                        <div className={styles.itemTitle}>
                          <span className={styles.itemName}>{item.name}</span>
                          <span className={styles.itemBadge}>
                            <Trans>produktów</Trans> {item.products_count}
                          </span>
                        </div>
                        <div className={styles.itemMeta}>
                          <Trans>Data utworzenia</Trans>:{' '}
                          {item.create_datetime &&
                            format(new Date(item.create_datetime), 'dd.MM.yyyy (HH:mm)')}
                        </div>
                      </div>

                      <div className={styles.itemActions}>
                        <button
                          type="button"
                          disabled={disabled}
                          className={classnames(styles.cartBtn, {
                            [styles.cartBtnDisabled]: disabled
                          })}
                          onClick={() => setCurrentShoppingListId(item.id)}
                          aria-label={t('Pokaz produkty') || 'show products'}>
                          <Cart />
                        </button>

                        {!item.blocked && (
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => setItemToRemove(item)}
                            aria-label={t('Usun liste') || 'delete list'}>
                            <Trash />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

            {!isListLoading && totalCount === 0 && (
              <div className={styles.emptyState}>
                <JournalText />
                <span>
                  <Trans>Brak list zakupowych</Trans>
                </span>
              </div>
            )}
          </div>

          <Link
            to="/dashboard/shopping-lists"
            onClick={() => setPopoverAnchor(null)}
            className={styles.seeMore}>
            <Trans>Zobacz więcej</Trans>
          </Link>
        </div>

        {itemToRemove && (
          <Modal title={t('Potwierdzenie usunięcia')} onClose={() => setItemToRemove(null)}>
            <div className={styles.confirmationModal}>
              <Trans>Czy na pewno chcesz usunąć liste zakupową</Trans>:{' '}
              <strong>{itemToRemove.name}</strong>
              <div className={styles.confirmationActions}>
                <Button onClick={() => setItemToRemove(null)} color="danger" ghost>
                  <Trans>Anuluj</Trans>
                </Button>
                <Button color="danger" loading={isDeleting} onClick={() => deleteShoppingList()}>
                  <Trans>Usuń</Trans>
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {currentShoppingListId && (
          <Modal
            title={t('Lista produktów')}
            onClose={() => setCurrentShoppingListId(null)}
            fullScreen={isMobile}>
            <WiseShoppingListPositions
              shoppingListId={currentShoppingListId}
              refetchShoppingLists={refetchShoppingLists}
            />
          </Modal>
        )}
      </Popover>
    </div>
  );
};

export default WiseShoppingListButton;
